import { Suspense, lazy, useEffect } from 'react';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { Box, Skeleton } from '@mui/material';

import { DemoProfile } from './types';
import { buildMockToken, buildMockUser } from './mockUser';
import DemoControlBar, { DEMO_CONTROL_BAR_HEIGHT } from './DemoControlBar';
import DemoDashboard from './DemoDashboard';
import DemoAccountingOverview from './DemoAccountingOverview';
import DemoReports from './DemoReports';
import DemoInvoicing from './DemoInvoicing';
import DemoGeneralLedger from './DemoGeneralLedger';
import DemoReferAndEarn from './DemoReferAndEarn';
import DemoTryItChecklist from './DemoTryItChecklist';
import DemoHelpAgentWidget from './DemoHelpAgentWidget';
import DemoAnalytics from './DemoAnalytics';
import DemoOnboardingFlowShell from './DemoOnboardingFlowShell';
import DemoBusinessVerification from './DemoBusinessVerification';

import { AuthContextProvider } from 'modules/Auth/AuthProvider';
import { IMCContextProvider } from 'modules/IMC/common/context';
import SnackbarProvider from 'modules/common/components/SnackbarProvider';
import RootStoreProvider from 'modules/common/stores/Root/Provider';
import { LayoutProvider } from 'modules/common/components/Layout/LayoutContext';
import browserHistory from 'modules/common/browserHistory';
import Layout from 'modules/common/components/Layout';
import lazyWithSuspenseAndRetry from 'modules/common/lazyWithSuspenseAndRetry';

// Lazy-load each module the demo exposes. These are the same modules the prod MemberRoutes mounts.
// Note: /dashboard renders the demo-specific DemoDashboard instead of the real Overview Tab —
// the prod Tab has an iframe-backed Financial Overview that shows blank in the demo.
const Documents = lazyWithSuspenseAndRetry(() => import('modules/Dashboard/Documents/Home/Tab'));
const Settings = lazyWithSuspenseAndRetry(() => import('modules/Settings'));
const Tax = lazyWithSuspenseAndRetry(() => import('modules/Tax/NewTaxRoutes'));
const Bookkeeping = lazyWithSuspenseAndRetry(() => import('modules/Dashboard/Bookkeeping'), true);
const Payroll = lazyWithSuspenseAndRetry(() => import('modules/Payroll'));
const Insurance = lazyWithSuspenseAndRetry(() => import('modules/Insurance'));
// Wraps the real Health page to intercept the Solo handoff CTAs with a branded "demo" dialog.
const DemoHealthInsurance = lazyWithSuspenseAndRetry(() => import('./DemoHealthInsurance'));
const Tasks = lazyWithSuspenseAndRetry(() => import('modules/Tasks'));

const NotFound = lazy(() => import('modules/App/NotFound'));

// Real onboarding flows, mounted full-screen outside the Layout like prod (modules/App/App.tsx).
const OnboardingRoutes = lazyWithSuspenseAndRetry(() => import('modules/Onboarding'));

export default function DemoApp({ profile }: { profile: DemoProfile }) {
    const user = buildMockUser(profile);
    const token = buildMockToken();

    // Warm the large onboarding chunk on idle so launching a flow isn't a cold import.
    useEffect(() => {
        const prefetch = () => {
            import('modules/Onboarding').catch(() => {
                // best-effort warm-up; the lazy import retries on real navigation
            });
        };
        const idle = (window as Window & { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
        if (idle) {
            idle(prefetch);
        } else {
            setTimeout(prefetch, 1500);
        }
    }, []);

    return (
        <AuthContextProvider user={user} token={token}>
            <SnackbarProvider>
                <IMCContextProvider>
                    <Router history={browserHistory}>
                        <DemoAnalytics />
                        <Switch>
                            {/* Full-screen flows; the shell adds Exit + auto-return on completion. */}
                            <Route
                                path="/onboarding"
                                render={() => (
                                    <DemoOnboardingFlowShell>
                                        <Switch>
                                            {/* Demo-specific business verification (clean, no AI
                                                pipeline). Real flows handle everything else. */}
                                            <Route
                                                path="/onboarding/business-verification"
                                                render={() => <DemoBusinessVerification profile={profile} />}
                                            />
                                            <Route component={OnboardingRoutes} />
                                        </Switch>
                                    </DemoOnboardingFlowShell>
                                )}
                            />
                            <Route>
                                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                                    <DemoControlBar profile={profile} />
                                    <Box
                                        sx={{
                                            flex: 1,
                                            minHeight: 0,
                                            // The prod Layout root hard-codes `height: 100dvh`. The demo stacks the
                                            // control bar above it, so without pinning it to this box the whole app
                                            // hangs below the fold by the bar's height — and anything anchored to the
                                            // app's bottom (the Message Center footer's "Schedule a call with us")
                                            // gets pushed off-screen with it.
                                            '& [aria-label="Main Page"]': { height: '100%' },
                                            // Same story one level down: the Message Center's paper is `position: fixed`
                                            // and MUI sizes it `calc(100% - 16px)` against the *viewport*, so it too
                                            // ignores the control bar and its bottom — plus the footer pinned there —
                                            // lands below the fold. Subtract the bar so the panel ends where the app does.
                                            '& .MuiDrawer-anchorRight.MuiDrawer-docked .MuiDrawer-paper': {
                                                height: `calc(100% - 16px - ${DEMO_CONTROL_BAR_HEIGHT}px)`,
                                            },
                                        }}>
                                        <RootStoreProvider>
                                            <LayoutProvider>
                                                <Layout>
                                                    <Suspense
                                                        fallback={
                                                            <Skeleton variant="rounded" width="100%" height="100%" />
                                                        }>
                                                        <Switch>
                                                            <Redirect exact from="/" to="/dashboard" />
                                                            <Route exact path="/dashboard" component={DemoDashboard} />
                                                            <Route exact path="/tasks" component={Tasks} />
                                                            <Route exact path="/documents" component={Documents} />
                                                            <Route path="/tax" component={Tax} />
                                                            {/* Override Accounting Overview — prod uses a HurdlrIframe that's empty in the demo. */}
                                                            <Route
                                                                exact
                                                                path="/bookkeeping/dashboard"
                                                                component={DemoAccountingOverview}
                                                            />
                                                            {/* Override Reports — prod renders Hurdlr-shaped JSON, blank without a Hurdlr session. */}
                                                            <Route
                                                                path="/bookkeeping/reports"
                                                                component={DemoReports}
                                                            />
                                                            {/* Override Invoicing — prod renders a Hurdlr iframe, blank in the demo. */}
                                                            <Route
                                                                path="/bookkeeping/invoicing"
                                                                component={DemoInvoicing}
                                                            />
                                                            {/* Override General Ledger — prod renders a Hurdlr iframe, blank in the demo. */}
                                                            <Route
                                                                exact
                                                                path="/bookkeeping/general-ledger"
                                                                component={DemoGeneralLedger}
                                                            />
                                                            <Route path="/bookkeeping" component={Bookkeeping} />
                                                            <Route path="/payroll" component={Payroll} />
                                                            {/* Health is prod-gated on the activated stage; the guard 404s
                                                                (and the nav locks) for launchpad/onboarding profiles. */}
                                                            <Route
                                                                path="/insurance/health"
                                                                component={DemoHealthInsurance}
                                                            />
                                                            <Route path="/insurance" component={Insurance} />
                                                            {/* Override Referrals — prod embeds Impact.com widgets that need a real JWT. */}
                                                            <Route path="/referrals" component={DemoReferAndEarn} />
                                                            <Route path="/settings" component={Settings} />
                                                            <Route component={NotFound} />
                                                        </Switch>
                                                    </Suspense>
                                                    {/* "Try it" checklist is for exploring the activated product — hide it mid-onboarding. */}
                                                    {profile.view === 'activated' && <DemoTryItChecklist />}
                                                    <DemoHelpAgentWidget />
                                                </Layout>
                                            </LayoutProvider>
                                        </RootStoreProvider>
                                    </Box>
                                </Box>
                            </Route>
                        </Switch>
                    </Router>
                </IMCContextProvider>
            </SnackbarProvider>
        </AuthContextProvider>
    );
}
